/**
 * AdminProfessionalsPanel — Admin-only listing of every Professional and
 * Entertainment-Pro signup. Mirrors the existing AdminVendors layout but
 * focused on the `role: professional` users.
 *
 * Backend: GET /api/admin/professionals
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Phone, MapPin, MagnifyingGlass, IdentificationBadge, Calendar, UserCircleGear } from '@phosphor-icons/react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Button } from './ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Entertainment category values are a subset of professional categories
// (set via PROFESSIONAL_GROUPS' entertainment group in registerData).
const ENTERTAINMENT_CATS = new Set([
  'dj', 'magician', 'anchor', 'dancer', 'live_band', 'orchestra',
  'nadaswaram_thavil', 'singer', 'mimicry', 'buffoon', 'birthday_artist',
]);

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

const AdminProfessionalsPanel = () => {
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');   // 'all' | 'pro' | 'entertainment' | 'empty'
  const [query, setQuery] = useState('');
  const [impersonatingId, setImpersonatingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/admin/professionals`, { withCredentials: true });
        setPros(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Could not load professionals');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const counts = useMemo(() => {
    const c = { all: pros.length, pro: 0, entertainment: 0, empty: 0 };
    pros.forEach((p) => {
      if (ENTERTAINMENT_CATS.has(p.category)) c.entertainment += 1;
      else c.pro += 1;
      if (p.is_empty_profile) c.empty += 1;
    });
    return c;
  }, [pros]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return pros.filter((p) => {
      if (filter === 'pro' && ENTERTAINMENT_CATS.has(p.category)) return false;
      if (filter === 'entertainment' && !ENTERTAINMENT_CATS.has(p.category)) return false;
      if (filter === 'empty' && !p.is_empty_profile) return false;
      if (!q) return true;
      return (
        (p.name || '').toLowerCase().includes(q)
        || (p.business_name || '').toLowerCase().includes(q)
        || (p.phone || '').toLowerCase().includes(q)
        || (p.category || '').toLowerCase().includes(q)
        || (p.location || '').toLowerCase().includes(q)
      );
    });
  }, [pros, filter, query]);

  const handleImpersonate = async (pro) => {
    if (impersonatingId) return;
    setImpersonatingId(pro.id);
    try {
      await axios.post(
        `${BACKEND_URL}/api/admin/impersonate/${pro.id}`,
        {},
        { withCredentials: true },
      );
      toast.success(`Now logged in as ${pro.business_name || pro.name} — use their dashboard Quick Add to seed packages.`);
      // Hard navigate so AuthContext re-fetches as the target user.
      window.location.assign('/dashboard');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Impersonation failed');
      setImpersonatingId(null);
    }
  };

  return (
    <Card className="bg-white border-[#E8E1D5]" data-testid="admin-pros-panel">
      <CardHeader>
        <CardTitle className="font-heading text-xl flex items-center gap-2">
          <IdentificationBadge size={20} weight="duotone" className="text-brand-primary" />
          Professionals &amp; Entertainment Pros
          <span className="text-xs font-normal text-text-secondary ml-1">({counts.all})</span>
        </CardTitle>
        <CardDescription>
          Every Professional and Entertainment-Pro that signed up. Filter by type,
          search by name / phone / category, see joining date.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Filter tabs */}
        <div className="flex flex-wrap items-center gap-2">
          {[
            ['all', 'All'],
            ['pro', 'Professionals'],
            ['entertainment', 'Entertainment'],
            ['empty', 'Empty Profile'],
          ].map(([k, label]) => (
            <button key={k} onClick={() => setFilter(k)}
              className={`px-3 py-1 text-xs font-bold uppercase tracking-wider border rounded-none ${filter === k ? 'bg-brand-primary text-sand-50 border-brand-primary' : 'border-[#E8E1D5] text-text-secondary hover:bg-stone-100'} ${k === 'empty' && counts.empty > 0 && filter !== k ? 'border-amber-400 text-amber-700' : ''}`}
              data-testid={`admin-pros-filter-${k}`}>
              {label} <span className="ml-1 opacity-70">({counts[k]})</span>
            </button>
          ))}
          <div className="relative flex-1 min-w-[180px]">
            <MagnifyingGlass size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-text-secondary" />
            <Input value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name / phone / category…"
              className="rounded-none h-8 text-xs pl-7"
              data-testid="admin-pros-search" />
          </div>
        </div>

        {/* List */}
        {loading ? (
          <p className="text-sm text-text-secondary py-3 text-center">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="text-sm text-text-secondary py-6 text-center" data-testid="admin-pros-empty">
            {pros.length === 0 ? 'No professionals have signed up yet.' : 'No professionals match your filter.'}
          </p>
        ) : (
          <div className="overflow-x-auto -mx-2">
            <table className="w-full text-sm" data-testid="admin-pros-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-text-secondary border-b border-[#E8E1D5]">
                  <th className="py-2 px-2 font-bold">Name / Business</th>
                  <th className="py-2 px-2 font-bold">Category</th>
                  <th className="py-2 px-2 font-bold">Phone</th>
                  <th className="py-2 px-2 font-bold">City</th>
                  <th className="py-2 px-2 font-bold">Joined</th>
                  <th className="py-2 px-2 font-bold">KYC</th>
                  <th className="py-2 px-2 font-bold">Profile</th>
                  <th className="py-2 px-2 font-bold text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => {
                  const isEnt = ENTERTAINMENT_CATS.has(p.category);
                  return (
                    <tr key={p.id} className="border-b border-[#F0EAE0] hover:bg-stone-50"
                      data-testid={`admin-pro-row-${p.id}`}>
                      <td className="py-2 px-2">
                        <div className="font-bold">{p.name || '—'}</div>
                        {p.business_name && (
                          <div className="text-[11px] text-text-secondary">{p.business_name}</div>
                        )}
                        {p.display_id && (
                          <div className="text-[10px] text-text-secondary">{p.display_id}</div>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        <Badge className={isEnt ? 'bg-purple-600 text-white' : 'bg-emerald-600 text-white'}>
                          {p.category || '—'}
                        </Badge>
                        {isEnt && (
                          <div className="text-[10px] uppercase tracking-wider text-purple-700 mt-0.5">Entertainment</div>
                        )}
                      </td>
                      <td className="py-2 px-2">
                        {p.phone ? (
                          <a href={`tel:${p.phone}`} className="text-brand-primary hover:underline inline-flex items-center gap-1"
                            data-testid={`admin-pro-phone-${p.id}`}>
                            <Phone size={11} /> {p.phone}
                          </a>
                        ) : '—'}
                      </td>
                      <td className="py-2 px-2">
                        {p.location ? (
                          <span className="inline-flex items-center gap-1 text-xs">
                            <MapPin size={11} className="text-text-secondary" /> {p.location}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="py-2 px-2 text-xs">
                        <span className="inline-flex items-center gap-1">
                          <Calendar size={11} className="text-text-secondary" /> {fmtDate(p.created_at)}
                        </span>
                      </td>
                      <td className="py-2 px-2 text-xs">
                        {p.kyc_status === 'approved' ? (
                          <Badge className="bg-emerald-600 text-white">Approved</Badge>
                        ) : p.kyc_status === 'pending' ? (
                          <Badge className="bg-amber-500 text-white">Pending</Badge>
                        ) : p.kyc_status === 'rejected' ? (
                          <Badge className="bg-rose-600 text-white">Rejected</Badge>
                        ) : (
                          <Badge variant="outline">—</Badge>
                        )}
                      </td>
                      <td className="py-2 px-2 text-xs">
                        {p.is_empty_profile ? (
                          <Badge className="bg-amber-500 text-white" data-testid={`admin-pro-empty-${p.id}`}>Empty</Badge>
                        ) : (
                          <Badge className="bg-emerald-600 text-white">Active</Badge>
                        )}
                      </td>
                      <td className="py-2 px-2 text-right">
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleImpersonate(p)}
                          disabled={!!impersonatingId}
                          className="bg-rose-700 hover:bg-rose-800 text-sand-50 rounded-none h-7 px-2 text-[10px] tracking-wider uppercase font-bold inline-flex items-center gap-1"
                          data-testid={`admin-pro-impersonate-${p.id}`}
                          title="Log in as this pro to seed packages on their behalf"
                        >
                          <UserCircleGear size={12} />
                          {impersonatingId === p.id ? 'Opening…' : 'Impersonate'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminProfessionalsPanel;
